import { Trans, useLingui } from '@lingui/react/macro';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  cn,
  buttonVariants,
} from '@pantry/ui';
import { useResyncAllGroups } from '@pantry/api-client';
import { toast } from 'sonner';

interface ResetToDefaultsDialogProps {
  householdId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Destructive confirmation dialog for resetting all product groups to system defaults.
 */
export function ResetToDefaultsDialog({
  householdId,
  open,
  onOpenChange,
}: ResetToDefaultsDialogProps) {
  const { t } = useLingui();
  const resyncAll = useResyncAllGroups();

  const handleReset = async () => {
    try {
      await resyncAll.mutateAsync({ householdId });
      toast.success(t`All groups reset to defaults`);
      onOpenChange(false);
    } catch {
      toast.error(t`Failed to reset groups`);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Trans context="dialog_title">Reset all groups to defaults?</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <Trans>
              This will delete all custom groups and restore all groups to system defaults. Shopping
              list items linked to deleted groups will be unlinked. This cannot be undone.
            </Trans>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            <Trans context="action_button">Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => void handleReset()}
            disabled={resyncAll.isPending}
            className={cn(buttonVariants({ variant: 'destructive' }))}
          >
            {resyncAll.isPending ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-destructive-foreground/30 border-t-destructive-foreground" />
                <Trans>Resetting…</Trans>
              </div>
            ) : (
              <Trans context="action_button">Reset All</Trans>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
